import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import SuperAdminNav from "../components/adNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useLocation, useNavigate, Link } from "react-router-dom";

const Admin_Members_list = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Read admin profile once
  const adminProfile = useMemo(() => {
    try {
      const raw = localStorage.getItem("nissi_admin");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Allowed user limit (0 means unlimited)
  const allowedUsers = useMemo(
    () => Number(adminProfile?.num_users || 0),
    [adminProfile]
  );

  useEffect(() => {
    fetchData();
  }, []);

  // Show alert if redirected back with limit exceeded
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromLimitParam = params.get("limit") === "1" || params.get("reason") === "limit";
    const fromState = location.state && location.state.limitExceeded;

    if (fromLimitParam || fromState) {
      Swal.fire({
        icon: "info",
        title: "User limit reached",
        text: "Please contact Nissi Infotech staff to increase your user capacity.",
      });

      // Clean up URL / state so it doesn't alert again on refresh
      if (fromLimitParam) {
        params.delete("limit");
        params.delete("reason");
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      } else if (fromState) {
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/admin_list");
      const rows = response.data.ListData || [];
      setData(rows);
      setFilteredData(rows);
    } catch (error) {
      console.error("Error fetching ListData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const r = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (!r.isConfirmed) return;

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      await axios.delete(`https://rfs2.infraconsoft.net.in/api/adminList_delete/${id}`);
      setData(prev => prev.filter((item) => item.fld_memberId !== id));
      setFilteredData(prev => prev.filter((item) => item.fld_memberId !== id));
      Swal.fire({ title: "Deleted!", text: "Your record has been deleted.", icon: "success" });
    } catch (error) {
      console.error("Error deleting Admin:", error);
      Swal.fire({ title: "Error!", text: "Failed to delete Admin.", icon: "error" });
    }
  };

  const handleUpdateApprove = async () => {
    const checkboxes = document.querySelectorAll('.admin-checkbox-input.approve');
    const selectedView = [];
    checkboxes.forEach((cb) => { if (cb.checked) selectedView.push(parseInt(cb.value)); });

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_ListApprove", { selectedView });
      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleUpdateDisApprove = async () => {
    const checkboxes = document.querySelectorAll('.admin-checkbox-input.disApprove');
    const selectedView = [];
    checkboxes.forEach((cb) => { if (cb.checked) selectedView.push(parseInt(cb.value)); });

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
      await axios.put("https://rfs2.infraconsoft.net.in/api/admin_ListDisApprove", { selectedView });
      Swal.fire("Success", "Permissions updated successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Error updating view permissions:", error);
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (!term) { setFilteredData(data); return; }

    const filtered = data.filter((item) =>
      String(item.fld_username || "").toLowerCase().includes(term) ||
      String(item.fld_branch_code || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
  };

  // Limit logic for Add New
  const currentCount = data.length; // assumes endpoint returns only this admin's users
  const hasLimit = allowedUsers > 0;
  const canAdd = !hasLimit || currentCount < allowedUsers;

  // IMPORTANT: Use a plain button and decide to navigate or alert.
  const handleAddClick = () => {
    if (canAdd) {
      navigate("/admin/admin_members_add");
    } else {
      Swal.fire({
        icon: "info",
        title: "User limit reached",
        text: `Your plan allows ${allowedUsers} user${allowedUsers > 1 ? "s" : ""}. Delete a user or contact Nissi Infotech staff to increase your user capacity.`,
      });
    }
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div id="admin-container">
      <SuperAdminNav />
      <div className="content-right">
        <section>
          <h2 className="add-txt">Admin Members List</h2>
          <Time />
        </section>

        {/* Add New Button + Counter (no Link, we navigate in code) */}
        <div className="add-new-container" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="add-new-btn"
            onClick={handleAddClick}
            // don't disable, just visually suggest it's capped
            style={!canAdd ? { opacity: 0.6 } : undefined}
            title={canAdd ? "Add a new user" : "You have reached your user limit"}
          >
            <i className="fa-solid fa-plus"></i> Add New
          </button>

          <span style={{ fontSize: 13, opacity: 0.8 }}>
            Users: <b>{currentCount}</b>
            {hasLimit ? <> / <b>{allowedUsers}</b></> : " / ∞"}
          </span>
        </div>

        {/* Search Filter */}
        <div className="search-filter">
          <p>Search: </p>
          <input
            type="text"
            placeholder="Search by User name"
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {/* Table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Branch</th>
                <th>Name</th>
                <th>User Name</th>
                <th>Password</th>
                <th>Designation</th>
                <th>Edit</th>
                <th>Permission</th>
                <th><button className="update-btn" onClick={handleUpdateApprove}> Approve</button></th>
                <th><button className="update-btn" onClick={handleUpdateDisApprove}> Dis Approve</button></th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((item, index) => (
                  <tr key={index}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>{item.fld_branch_code}</td>
                    <td>{item.fld_name}</td>
                    <td>{item.fld_username}</td>
                    <td>{item.fld_pass}</td>
                    <td>{item.fld_designation}</td>
                    <td><Link to={`/admin/admin_members_edit/${item.fld_memberId}`}>Edit</Link></td>
                    <td><Link to={`/admin/admin_members_approval/${item.fld_memberId}`}>Link</Link></td>

                    <td>
                      {item.fld_Approve?.toLowerCase().trim() === 'yes' ? (
                        <span style={{color: 'green', fontWeight: 'bold'}}>Approved</span>
                      ) : (
                        <input type="checkbox" value={item.SlNo} className="admin-checkbox-input approve" />
                      )}
                    </td>

                    <td>
                      {item.fld_Approve?.toLowerCase().trim() === 'no' ? (
                        <span style={{color: 'red', fontWeight: 'bold'}}>Dis Approved</span>
                      ) : (
                        <input type="checkbox" value={item.SlNo} className="admin-checkbox-input disApprove" />
                      )}
                    </td>

                    <td className="del">
                      <i className="fa-solid fa-trash-can" onClick={() => handleDelete(item.fld_memberId)}></i>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>
          <button onClick={() => paginate(1)} className={currentPage === 1 ? 'active' : ''}>
            1
          </button>
          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button key={pageNumber} onClick={() => paginate(pageNumber)} className={currentPage === pageNumber ? 'active' : ''}>
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })
          }
          <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Admin_Members_list;
